import React, { useState, useEffect, useRef } from 'react'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import QRCode from 'qrcode'
import { useSessionCleanup } from '@renderer/stores/sessionState'
import Keyboard from 'simple-keyboard'
import 'simple-keyboard/build/css/index.css'
import { printImmediately } from '@renderer/utils/pipeline/printService'
import { usePrintStore } from '@renderer/stores/printStore'
import cogoToast from '@successtar/cogo-toast'
import { useTranslation } from 'react-i18next'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useConfigStore } from '@renderer/stores/configState'
import { resolveGallerySessionUrl } from '@renderer/utils/galleryUrlUtils'

type ResultState = 'input-email' | 'printing'

const Result: React.FC = () => {
  const { t } = useTranslation()
  const { id, userPreferences, setSessionState, uploadSessionState, logActivity, gallery } =
    useSessionStateStore()
  const sessionCleanup = useSessionCleanup()
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()
  const { config: globalConfig } = useConfigStore()
  const resultPageSettings = globalConfig?.pages?.result?.settings

  const [currentState, setCurrentState] = useState<ResultState>('input-email')
  const [qrCodeImage, setQrCodeImage] = useState('')
  const [email, setEmail] = useState(userPreferences.email || '')
  const [phoneNumber, setPhoneNumber] = useState(userPreferences.phoneNumber || '')
  const [currentInput, setCurrentInput] = useState<'email' | 'phone'>('email')

  const keyboardRef = useRef<Keyboard | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const currentInputRef = useRef<'email' | 'phone'>('email')
  const emailRef = useRef<string>(userPreferences.email || '')
  const phoneNumberRef = useRef<string>(userPreferences.phoneNumber || '')

  // Update refs when state changes
  useEffect(() => {
    currentInputRef.current = currentInput
  }, [currentInput])

  useEffect(() => {
    emailRef.current = email
  }, [email])

  useEffect(() => {
    phoneNumberRef.current = phoneNumber
  }, [phoneNumber])

  // Generate QR code on mount
  useEffect(() => {
    if (!id) return
    const galleryTarget = resolveGallerySessionUrl(id, resultPageSettings)
    QRCode.toDataURL(galleryTarget, {
      margin: 0,
      width: 300
    }).then(setQrCodeImage)
  }, [id, resultPageSettings?.galleryPublicBaseUrl])

  // Initialize keyboard (only once)
  useEffect(() => {
    keyboardRef.current = new Keyboard({
      onChange: (input: string): void => {
        if (currentInputRef.current === 'email') {
          setEmail(input)
        } else {
          setPhoneNumber(input)
        }
      },
      onKeyPress: (button: string): void => {
        if (button === '{bksp}') {
          // Handled by onChange
        } else if (button === '{enter}') {
          // Switch between email and phone input
          setCurrentInput(currentInputRef.current === 'email' ? 'phone' : 'email')
        }
      },
      layout: {
        default: [
          '` 1 2 3 4 5 6 7 8 9 0 - = {bksp}',
          '{tab} q w e r t y u i o p { } |',
          '{lock} a s d f g h j k l : " {enter}',
          '{shift} z x c v b n m < > ? {shift}',
          '.com @ {space}'
        ]
      },
      theme: 'hg-theme-default hg-layout-default myTheme',
      display: {
        '{bksp}': '⌫',
        '{enter}': '✓',
        '{space}': ' ',
        '{shift}': '⇧',
        '{tab}': '⇥',
        '{lock}': '⇪'
      },
      input: currentInputRef.current === 'email' ? emailRef.current : phoneNumberRef.current
    })

    return (): void => {
      keyboardRef.current?.destroy()
    }
  }, []) // Empty dependency array - keyboard is created only once

  // Update keyboard input when currentInput, email, or phoneNumber changes
  useEffect(() => {
    if (keyboardRef.current) {
      const newInput = currentInput === 'email' ? email : phoneNumber
      keyboardRef.current.setInput(newInput)
    }
  }, [currentInput, email, phoneNumber])

  // Handle printing state timeout
  useEffect(() => {
    if (currentState === 'printing') {
      timeoutRef.current = setTimeout(() => {
        sessionCleanup()
      }, 15000)
    }

    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [currentState, sessionCleanup])

  const handleInputFocus = (inputType: 'email' | 'phone'): void => {
    setCurrentInput(inputType)
    // Update keyboard input immediately when switching focus
    if (keyboardRef.current) {
      keyboardRef.current.setInput(inputType === 'email' ? email : phoneNumber)
    }
  }

  // Handle input changes from keyboard
  const handleInputChange = (inputType: 'email' | 'phone', value: string): void => {
    if (inputType === 'email') {
      setEmail(value)
    } else {
      setPhoneNumber(value)
    }
  }

  const handlePrint = async (): Promise<void> => {
    // Debug: Log the state before any changes
    console.log('Before setSessionState - capturedItems:', gallery.capturedItems)

    // Save email and phone to session state
    setSessionState({
      userPreferences: {
        email,
        phoneNumber
      }
    })

    // Debug: Log the state after setSessionState
    console.log(
      'After setSessionState - capturedItems:',
      useSessionStateStore.getState().gallery.capturedItems
    )

    logActivity('Print button clicked with valid email and phone')

    setCurrentState('printing')

    try {
      // Get strip URL directly from gallery
      const stripUrl = gallery.stripUrl

      console.log('gallery.stripUrl', stripUrl)

      if (!stripUrl) {
        throw new Error('No strip found in session state')
      }

      // Get the appropriate printer settings based on the selected frame
      const { printerName, settings } = getPrinterForFrame(userPreferences.frameId || '')

      if (isPrintingEnabled && userPreferences.usePrint) {
        logActivity('Sending print job', { copies: userPreferences.copies || 1, printerName })

        // Convert strip URL to a blob for printing
        const response = await fetch(stripUrl)
        const blob = await response.blob()

        printImmediately(
          blob,
          printerName,
          userPreferences.copies || 1,
          settings,
          () => cogoToast.success(t('select.printInitiated', 'Printing Initialized')),
          (errorMsg) =>
            cogoToast.error(`${t('select.printFailed', 'Failed to print')}: ${errorMsg}`)
        )
      }

      // Upload session state with email and phone
      uploadSessionState()
    } catch (error) {
      console.error('Print error:', error)
      cogoToast.error('Failed to process print job')
    }
  }

  // Call all hooks at the top level (before any early returns)
  const cachedQrCodeImage = useCachedImage(qrCodeImage)

  if (currentState === 'printing') {
    return (
      <div className="w-full h-full bg-white flex items-center justify-center">
        <div className="text-center space-y-8">
          <h1 className="text-6xl font-bold text-blueGrayYotta mb-4">
            Thanks for using Photobooth !
          </h1>
          <h2 className="text-4xl text-blueGrayYotta mb-4">
            we&apos;ve sent the photos to your email
          </h2>
          <h3 className="text-3xl text-blueGrayYotta mb-8">printing...</h3>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blueGrayYotta border-t-transparent"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full bg-white flex flex-col">
      {/* Top Half - Email Input */}
      <div className="flex-1 p-8 justify-center items-center flex gap-64">
        {/* Left Side - QR Code */}
        <div className="flex flex-col items-center justify-center">
          <div className="bg-white p-6 rounded-2xl shadow-xl flex items-center justify-center">
            {qrCodeImage ? (
              <img className="w-64 h-64" src={cachedQrCodeImage} alt="QR Code" />
            ) : (
              <div className="w-64 h-64 flex items-center justify-center">
                <svg
                  className="w-16 h-16 animate-spin text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            )}
          </div>
          <p className="text-lg text-gray-600 mt-4">*scan untuk soft file</p>
        </div>

        {/* Right Side - Form */}
        <div className="flex flex-col items-center justify-center px-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-8">Mohon Isi Dengan Benar</h1>

          <div className="w-full max-w-md space-y-6">
            {/* Email Input */}
            <div>
              <label
                className={`block text-xl font-semibold mb-2 ${
                  currentInput === 'email' ? 'text-blueGrayYotta' : 'text-gray-700'
                }`}
              >
                Email {currentInput === 'email' && <span className="text-sm">(Active)</span>}
              </label>
              <input
                type="text"
                value={email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onFocus={() => handleInputFocus('email')}
                className={`w-full p-4 text-xl border-2 rounded-lg focus:outline-none ${
                  currentInput === 'email' ? 'border-blueGrayYotta bg-blue-50' : 'border-gray-300'
                }`}
                placeholder="Enter email"
              />
            </div>

            {/* Phone Input */}
            <div>
              <label
                className={`block text-xl font-semibold mb-2 ${
                  currentInput === 'phone' ? 'text-blueGrayYotta' : 'text-gray-700'
                }`}
              >
                No Telepon {currentInput === 'phone' && <span className="text-sm">(Active)</span>}
              </label>
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                onFocus={() => handleInputFocus('phone')}
                className={`w-full p-4 text-xl border-2 rounded-lg focus:outline-none ${
                  currentInput === 'phone' ? 'border-blueGrayYotta bg-blue-50' : 'border-gray-300'
                }`}
                placeholder="Enter phone number"
              />
            </div>

            {/* Print Button */}
            <button
              onClick={handlePrint}
              className={`w-full py-4 text-2xl font-bold text-white rounded-lg transition-all duration-300 ${
                email.length > 0 && phoneNumber.length > 0
                  ? 'bg-blueGrayYotta hover:bg-opacity-90 hover:scale-105'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Half - On Screen Keyboard */}
      <div className="flex-1 bg-plaster p-4 flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div id="keyboard" className="simple-keyboard w-full max-w-4xl" />
        </div>
      </div>

      <style>
        {`
          .simple-keyboard {
            background: transparent;
          }
          .simple-keyboard.myTheme {
            background: transparent;
            border-radius: 0;
          }
          .simple-keyboard.myTheme .hg-button {
            height: 60px;
            display: flex;
            justify-content: center;
            align-items: center;
            background: rgba(255, 255, 255, 0.95);
            color: #333333;
            border-radius: 12px;
            font-size: 24px;
            font-family: inherit;
            margin: 2px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            transition: all 0.2s ease;
          }
          .simple-keyboard.myTheme .hg-button:hover {
            background: rgba(255, 255, 255, 1);
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          }
          .simple-keyboard.myTheme .hg-button:active {
            background: #1c84ff;
            color: white;
            transform: translateY(0);
          }
          .simple-keyboard.myTheme .hg-button.hg-standardBtn {
            background: rgba(255, 255, 255, 0.95);
          }
          .simple-keyboard.myTheme .hg-button.hg-functionBtn {
            background: rgba(255, 255, 255, 0.8);
          }
          .simple-keyboard.myTheme .hg-button.hg-functionBtn:hover {
            background: rgba(255, 255, 255, 0.9);
          }
        `}
      </style>
    </div>
  )
}

export default Result
